import React, { useEffect, useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardBody, Container, Button, Input, Label, Row, Col } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import SearchableSelect from "../../../Components/Common/SearchableSelect";
import FilterCard from "../../../Components/Common/FilterCard";
import { DataTable, Column } from "../../../Components/Common/DataTable";
import { getTodayDate, getDateMonthsAgo } from "../../../utils/dateUtils";
import { exportEtatsPdf } from "../../../utils/exportEtatsPdf";
import { exportEtatsExcel } from "../../../utils/exportEtatsExcel";
import logoAgerouteLight from "../../../assets/images/logo-ageroute-light.png";
import {
  useCommunesQuery,
  usePrioritesQuery,
  useStatutAlertesQuery,
  usePointHebdomadaireQuery,
  useChefEquipesQuery,
  invalidateEtatsQueries,
  type PointHebdomadairePayload,
} from "../../../hooks/queries/useRoadAlertQueries";

interface EtatRow extends Record<string, any> {
  itineraire?: string;
  libellestatut?: string;
  surface?: number;
  chefEquipe?: string;
  datecreation?: string;
  libellecommune?: string;
}

const defaultFilters = {
  idCommune: "",
  idPriorite: "",
  dateDebut: getDateMonthsAgo(3),
  dateFin: getTodayDate(),
  idStatutAlerte: "",
  traitePar: "",
};

const Etats = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<typeof defaultFilters | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!logoAgerouteLight) return;
    fetch(logoAgerouteLight)
      .then((r) => r.blob())
      .then((blob) => new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(blob);
      }))
      .then(setLogoDataUrl)
      .catch(() => {});
  }, []);

  const communesQuery = useCommunesQuery();
  const prioritesQuery = usePrioritesQuery();
  const statutAlertesQuery = useStatutAlertesQuery();
  const chefEquipesQuery = useChefEquipesQuery();

  const communes = useMemo(() => {
    const d = communesQuery.data;
    return Array.isArray(d) ? d : [];
  }, [communesQuery.data]);
  const priorites = useMemo(() => {
    const d = prioritesQuery.data;
    return Array.isArray(d) ? d : [];
  }, [prioritesQuery.data]);
  const statutAlertes = useMemo(() => statutAlertesQuery.data ?? [], [statutAlertesQuery.data]);
  const chefEquipes = useMemo(() => chefEquipesQuery.data ?? [], [chefEquipesQuery.data]);

  const pointHebdomadairePayload = useMemo((): PointHebdomadairePayload | null => {
    if (!appliedFilters) return null;
    return {
      data: {
        idCommune: appliedFilters.idCommune || "",
        traitePar: appliedFilters.traitePar || "",
        idStatutAlerte: appliedFilters.idStatutAlerte || "",
        idPriorite: appliedFilters.idPriorite || "",
        dateCreationParam: {
          operator: "[]",
          start: appliedFilters.dateDebut,
          end: appliedFilters.dateFin,
        },
      },
    };
  }, [appliedFilters]);

  const pointHebdomadaireQuery = usePointHebdomadaireQuery(pointHebdomadairePayload);
  const data = useMemo(() => (pointHebdomadaireQuery.data ?? []) as EtatRow[], [pointHebdomadaireQuery.data]);
  const loading = pointHebdomadaireQuery.isLoading;

  useEffect(() => {
    setAppliedFilters(defaultFilters);
  }, []);

  const handleSearch = () => {
    invalidateEtatsQueries(queryClient);
    setAppliedFilters(filters);
  };

  const handleExportPDF = () => {
    const periodDebut = appliedFilters?.dateDebut ?? filters.dateDebut;
    const periodFin = appliedFilters?.dateFin ?? filters.dateFin;
    exportEtatsPdf({
      data,
      dateDebut: periodDebut,
      dateFin: periodFin,
      filename: `rapport-etats-${periodDebut}-${periodFin}.pdf`,
      logoDataUrl: logoDataUrl || undefined,
    });
  };

  const handleExportExcel = () => {
    const periodDebut = appliedFilters?.dateDebut ?? filters.dateDebut;
    const periodFin = appliedFilters?.dateFin ?? filters.dateFin;
    exportEtatsExcel({
      data,
      dateDebut: periodDebut,
      dateFin: periodFin,
      filename: `rapport-etats-${periodDebut}-${periodFin}.xlsx`,
      logoDataUrl: logoDataUrl || undefined,
    });
  };

  // Configuration des colonnes
  const columns: Column<EtatRow>[] = useMemo(() => [
    {
      key: "datecreation",
      label: "Date création",
      sortable: true,
      visible: true,
      render: (_: unknown, row?: EtatRow) => {
        const date = row?.datecreation || "-";
        if (date === "-") return <span className="text-gray-700">-</span>;
        // Formater la date YYYY-MM-DD en DD/MM/YYYY
        try {
          const [year, month, day] = date.split("-");
          const formattedDate = `${day}/${month}/${year}`;
          return <span className="text-gray-700">{formattedDate}</span>;
        } catch {
          return <span className="text-gray-700">{date}</span>;
        }
      },
    },
    {
      key: "libellecommune",
      label: "Commune",
      sortable: true,
      visible: true,
      render: (_: unknown, row?: EtatRow) => (
        <span className="text-gray-700">
          {row?.libellecommune || "-"}
        </span>
      ),
    },
    {
      key: "itineraire",
      label: "Itinéraire",
      sortable: true,
      visible: true,
      render: (_: unknown, row?: EtatRow) => (
        <span className="text-gray-700">
          {row?.itineraire || "-"}
        </span>
      ),
    },
    {
      key: "surface",
      label: "Surface",
      sortable: true,
      visible: true,
      render: (_: unknown, row?: EtatRow) => {
        const surface = row?.surface;
        if (surface == null) return <span className="text-gray-700">-</span>;
        return (
          <span className="text-gray-700">
            {typeof surface === "number" ? surface.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : surface}
          </span>
        );
      },
    },
    {
      key: "libellestatut",
      label: "Statut",
      sortable: true,
      visible: true,
      render: (_: unknown, row?: EtatRow) => {
        const status = row?.libellestatut || "-";
        const getStatusColor = (status: string) => {
          if (status.toLowerCase().includes("termin")) return "bg-success text-white";
          if (status.toLowerCase().includes("cours") || status.toLowerCase().includes("en cours")) return "bg-info text-white";
          if (status.toLowerCase().includes("pause")) return "bg-warning text-dark";
          return "bg-secondary text-white";
        };
        
        return (
          <span className={`px-2 py-1 rounded-pill text-xs fw-medium d-inline-block ${getStatusColor(status)}`}>
            {status}
          </span>
        );
      },
    },
    {
      key: "chefEquipe",
      label: "Chef équipe",
      sortable: true,
      visible: true,
      render: (_: unknown, row?: EtatRow) => (
        <span className="text-gray-700">
          {row?.chefEquipe || "-"}
        </span>
      ),
    },
  ], []);

  // Options pour les selects searchable (même logique que filter-hook AlertesList)
  const communeOptions = useMemo(() => {
    const list = (Array.isArray(communes) ? communes : []) as Record<string, unknown>[];
    const map = new Map<string, { value: string; label: string }>();
    map.set("", { value: "", label: "-Tous-" });
    list.forEach((c) => {
      const value = String(c?.idCommune ?? c?.id ?? "");
      const label = String(c?.libelleCommune ?? c?.libelle ?? "Commune sans nom");
      if (value !== "" && !map.has(value)) map.set(value, { value, label });
    });
    return Array.from(map.values());
  }, [communes]);

  const prioriteOptions = useMemo(() => {
    const list = (Array.isArray(priorites) ? priorites : []) as Record<string, unknown>[];
    const map = new Map<string, { value: string; label: string }>();
    map.set("", { value: "", label: "-Tous-" });
    list.forEach((p) => {
      const value = String(p?.id ?? p?.idPriorite ?? p?.value ?? "");
      const label = String(p?.libellePriorite ?? p?.libelle ?? p?.label ?? (value ? `Priorité ${value}` : "-"));
      if (value !== "" && !map.has(value)) map.set(value, { value, label });
    });
    return Array.from(map.values());
  }, [priorites]);

  const statusOptions = useMemo(() => {
    const list = (Array.isArray(statutAlertes) ? statutAlertes : []) as Record<string, unknown>[];
    const first = list[0];
    const idKey = list.length && first?.idStatutAlerte != null ? "idStatutAlerte" : "id";
    const libKey = list.length && first?.libelleStatutAlerte != null ? "libelleStatutAlerte" : first?.libelle != null ? "libelle" : "label";
    return [
      { value: "", label: "-Tous-" },
      ...list.map((s) => ({
        value: String(s[idKey] ?? ""),
        label: String(s[libKey] ?? s[idKey] ?? "-"),
      })),
    ];
  }, [statutAlertes]);

  const chefEquipeOptions = useMemo(() => {
    const list = Array.isArray(chefEquipes) ? chefEquipes : [];
    
    return [
      { value: "", label: "-tous-" },
      ...list.map((chef: Record<string, unknown>) => {
        // Utiliser username en priorité pour la valeur (comme dans AffectationEquipeModal)
        // Sinon utiliser idUsers ou id comme fallback
        const value = chef.idChefEquipe;
        // Pour l'affichage, utiliser name, libelleChefEquipe, ou username
        const label = chef.leChefEquipe;
        
        return {
          value: String(value),
          label: String(label),
        };
      }),
    ];
  }, [chefEquipes]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Etats" pageTitle="Etats" />
          
          <FilterCard title="Filtres de recherche">
            <Row className="g-3 align-items-end">
                <Col md={2}>
                  <Label className="small text-muted mb-1"><i className="ri-map-pin-line me-1" />Commune</Label>
                  <SearchableSelect
                    options={communeOptions}
                    value={filters.idCommune || null}
                    onChange={(value) => {
                      const newValue = value !== null && value !== undefined ? String(value) : "";
                      setFilters({ ...filters, idCommune: newValue });
                    }}
                    placeholder="-Tous-"
                    isLoading={communesQuery.isLoading}
                  />
                </Col>
                <Col md={2}>
                  <Label className="small text-muted mb-1"><i className="ri-flag-line me-1" />Priorité</Label>
                  <SearchableSelect
                    options={prioriteOptions}
                    value={filters.idPriorite || null}
                    onChange={(value) => {
                      const newValue = value !== null && value !== undefined ? String(value) : "";
                      setFilters({ ...filters, idPriorite: newValue });
                    }}
                    placeholder="-Tous-"
                    isLoading={prioritesQuery.isLoading}
                  />
                </Col>
                <Col md={2}>
                  <Label className="small text-muted mb-1"><i className="ri-calendar-line me-1" />Date début</Label>
                  <Input type="date" className="form-control rounded-3" value={filters.dateDebut} onChange={(e) => setFilters({ ...filters, dateDebut: e.target.value })} />
                </Col>
                <Col md={2}>
                  <Label className="small text-muted mb-1"><i className="ri-calendar-line me-1" />Date fin</Label>
                  <Input type="date" className="form-control rounded-3" value={filters.dateFin} onChange={(e) => setFilters({ ...filters, dateFin: e.target.value })} />
                </Col>
                <Col md={2}>
                  <Label className="small text-muted mb-1"><i className="ri-list-check me-1" />Statut alerte</Label>
                  <SearchableSelect
                    options={statusOptions}
                    value={filters.idStatutAlerte || null}
                    onChange={(value) => {
                      const newValue = value !== null && value !== undefined ? String(value) : "";
                      setFilters({ ...filters, idStatutAlerte: newValue });
                    }}
                    placeholder="-Tous-"
                  />
                </Col>
                <Col md={2}>
                  <Label className="small text-muted mb-1"><i className="ri-user-line me-1" />Traité par</Label>
                  <SearchableSelect
                    options={chefEquipeOptions}
                    value={filters.traitePar || null}
                    onChange={(value) => {
                      const newValue = value !== null && value !== undefined ? String(value) : "";
                      setFilters({ ...filters, traitePar: newValue });
                    }}
                    placeholder="-tous-"
                  />
                </Col>
              </Row>

              {/* Boutons d'action */}
              <Row className="mt-3">
                <Col md={12} className="d-flex gap-2 flex-wrap">
                  <Button className="rounded-pill" color="secondary" onClick={handleSearch}>
                    <i className="ri-search-line me-1" />Rechercher
                  </Button>
                  <Button className="rounded-pill btn-ageroute-orange shadow-sm" onClick={handleExportPDF}>
                    <i className="ri-file-pdf-line me-1" />PDF
                  </Button>
                  <Button className="rounded-pill btn-ageroute-orange shadow-sm" onClick={handleExportExcel}>
                    <i className="ri-file-excel-2-line me-1" />Excel
                  </Button>
                </Col>
              </Row>
          </FilterCard>

          {/* Tableau de données */}
          <Card className="card-ageroute rounded-3 shadow-sm border-0 overflow-hidden">
            <CardBody className="p-3 p-md-4">
              <DataTable
                instanceId="etats-table"
                data={data}
                columns={columns}
                isLoading={loading}
                pagination={true}
                pageSize={10}
                pageSizeOptions={[10, 20, 50, 100]}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Etats;
